import { ThunkDispatch } from '@reduxjs/toolkit';
import { Map } from 'immutable';
import { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  documentsSlice,
  fetchDocuments,
  fromDocuments,
  saveDocumentsOrder,
  addDocument,
  updateDocument,
} from './documents';
import { Document } from './documentsTypes';
import { useDebouncedCallback } from 'utils/debounce-promise';

/**
 * A custom hook that fetches and manages documents for an event by its ID.
 * The hook automatically fetches the documents on mount and provides a way to refetch them.
 *
 * @param {string} eventId - The unique identifier of the event to fetch documents for
 * @param {string} [recipient] - Optional filter for document recipient ('teams', 'judges', or 'teams_and_judges')
 * @returns {Object} An object containing:
 *   - documents: The documents data from the Redux store
 *   - loading: A boolean indicating if documents are currently being fetched
 *   - refetchDocuments: A function to manually trigger a refresh of the documents data
 *
 * @example
 * const { documents, loading, refetchDocuments } = useFetchDocuments('123', 'teams');
 * console.log(documents);
 * refetchDocuments();
 */
export const useFetchDocuments = (eventId: string) => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();

  const documents = useSelector((state: Map<string, any>) =>
    fromDocuments.getDocuments(state?.toJS(), eventId),
  );

  const loading = useSelector((state: Map<string, any>) =>
    fromDocuments.getIsLoading(state?.toJS()),
  );

  const refetchDocuments = useCallback(() => {
    dispatch(fetchDocuments({ eventId }));
  }, [dispatch, eventId]);

  useEffect(() => {
    refetchDocuments();
  }, [refetchDocuments]);

  return { documents, loading, refetchDocuments };
};

/**
 * A custom React hook for saving the order of documents in an event and tracking the saving state.
 *
 * This hook provides a function to save documents order by dispatching a saveDocumentsOrder action,
 * and a boolean flag indicating whether a save operation is currently in progress.
 *
 * @returns {{ saving: boolean, saveOrder: (eventId: string, documentIds: number[]) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if a save documents order operation is currently in progress.
 *   - saveOrder: A function that takes eventId and documentIds array and dispatches a saveDocumentsOrder action to the store.
 *
 * @example
 * const { saveOrder, saving } = useSaveDocumentsOrder();
 * await saveOrder('event123', ['document1', 'document2', 'document3']);
 * if (saving) {
 *    Show a loading indicator while the order is being saved
 * }
 */
export const useSaveDocumentsOrder = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSelector((state: Map<string, any>) => fromDocuments.getIsSaving(state?.toJS()));

  const saveOrder = useCallback(
    (eventId: string, documentIds: number[]) =>
      dispatch(saveDocumentsOrder({ eventId, documentIds })),
    [dispatch],
  );

  return { saving, saveOrder };
};

/**
 * A custom React hook for adding a document to an event and tracking the saving state.
 *
 * This hook provides a function to add a document by dispatching an addDocument action,
 * and a boolean flag indicating whether a save operation is currently in progress.
 *
 * @returns {{ saving: boolean, addDocumentToEvent: (eventId: string, document: Document) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if an add document operation is currently in progress.
 *   - addDocumentToEvent: A function that takes eventId and document data and dispatches an addDocument action to the store.
 *
 * @example
 * const { addDocumentToEvent, saving } = useAddDocument();
 * await addDocumentToEvent('event123', { title: 'Doc Title', instructions: 'Doc instructions' });
 * if (saving) {
 *    Show a loading indicator while the document is being saved
 * }
 */
export const useAddDocument = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSelector((state: Map<string, any>) => fromDocuments.getIsSaving(state?.toJS()));

  const addDocumentToEvent = useCallback(
    (eventId: string, document: Document) => dispatch(addDocument({ eventId, document })),
    [dispatch],
  );

  return { saving, addDocumentToEvent };
};

/**
 * A custom React hook for updating a document in an event and tracking the saving state.
 *
 * This hook provides a function to update a document by dispatching an updateDocument action,
 * and a boolean flag indicating whether a save operation is currently in progress.
 *
 * @returns {{ saving: boolean, updateDocumentInEvent: (eventId: string, documentId: number, document: Document) => Promise<any> }} An object containing:
 *   - saving: A boolean indicating if an update document operation is currently in progress.
 *   - updateDocumentInEvent: A function that takes eventId, documentId and document data and dispatches an updateDocument action to the store.
 *
 * @example
 * const { updateDocumentInEvent, saving } = useUpdateDocument();
 * await updateDocumentInEvent('event123', 456, { title: 'Updated Title', instructions: 'Updated instructions' });
 * if (saving) {
 *    Show a loading indicator while the document is being updated
 * }
 */
export const useUpdateDocument = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const saving = useSelector((state: Map<string, any>) => fromDocuments.getIsSaving(state?.toJS()));

  const updateDocumentInEvent = useCallback(
    (eventId: string, documentId: number, document: Document) =>
      dispatch(updateDocument({ eventId, documentId, document })),
    [dispatch],
  );

  return { saving, updateDocumentInEvent };
};

/**
 * A custom React hook for reordering documents in an event.
 *
 * This hook provides a function to reorder documents by moving a document from one position
 * to another in the documents array of an event.
 *
 * @returns {{ reorderDocuments: (eventId: string, fromIndex: number, toIndex: number) => void }} An object containing:
 *   - reorderDocuments: A function that takes eventId, fromIndex, and toIndex and reorders the documents array.
 *
 * @example
 * const { reorderDocuments } = useReorderDocuments();
 * reorderDocuments('event123', 2, 0); // Moves document from position 2 to position 0
 */
export const useReorderDocuments = () => {
  const dispatch = useDispatch<ThunkDispatch<any, any, any>>();
  const { saveOrder, saving } = useSaveDocumentsOrder();

  const debouncedSaveOrder = useDebouncedCallback(
    async (...args: any[]) => {
      const [eventId, documents] = args as [string, Document[]];
      saveOrder(
        eventId,
        documents.map((document) => document.id),
      );
    },
    [],
    1000,
  );

  const reorderDocuments = useCallback(
    (eventId: string, fromIndex: number, toIndex: number) => {
      const result = dispatch(
        documentsSlice.actions.reorderDocuments({ eventId, fromIndex, toIndex }),
      );
      return debouncedSaveOrder(eventId, result.payload.documents);
    },
    [dispatch, saveOrder],
  );

  return { reorderDocuments, saving };
};
